import { Elements } from "@stripe/react-stripe-js";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { api1 } from "../../utils/axiosInstance";
import useCourseList from "../../utils/useCourseList";
import { trackExternalLinkClick } from '../../utils/trackExternalLinks';
import SecondaryLoader from "../SecondaryLoader/SecondaryLoader";
import CustomButton from "../customButtom/CustomButton";
import UpskillingPathway from "../learningPathway/UpskillingPathway";
import VirtualCourses from "../virtualCourses/VirtualCourses";
import WelcomeCard from "../WelcomeCard";
import { stripePromise } from "../../constants/constants";

const RecommendedCoursesPage = () => {
  const filters = ["All", "Started", "In Progress", "Completed"];
  const [bookmarkedCourseIds, setBookmarkedCourseIds] = useState(new Set());
  const [filteredCourses, setFilteredCourses] = useState();
  const [selectedFilter, setSelectedFilter] = useState("All"); // Track selected filter

  const [myCourses, setMyCourses] = useState([]);
  const { currentJob, targetJob } = useSelector((state) => state.user);
  const userId = useSelector((state) => state.auth.id);
  const navigate = useNavigate();

  const {
    data: courseList,
    isPending: courseListPending,
    error,
  } = useCourseList(targetJob);

  const handleClick = (url, courseTitle, partner) => {
    trackExternalLinkClick(url, courseTitle, partner);
  };

  useEffect(() => {
    const fetchBookmarkedCourses = async () => {
      try {
        const response = await api1.get(
          `/api/courses/get-bookmark-courses/${userId}`
        );
        const bookmarkedCourses = response.data.data;
        setMyCourses(bookmarkedCourses);
        setFilteredCourses(bookmarkedCourses); //this needs to be updated when I change the status of the course

        const bookmarkedIds = new Set(
          bookmarkedCourses.map((course) => course.courseId)
        );
        setBookmarkedCourseIds(bookmarkedIds);
      } catch (error) {
        console.error("Error fetching bookmarks", error);
      }
    };

    fetchBookmarkedCourses();
  }, [userId]);
  const handleBookmarkToggle = (course, isCurrentlyBookmarked) => {
    // Update bookmarkedCourseIds Set
    const newBookmarkedIds = new Set(bookmarkedCourseIds);

    if (isCurrentlyBookmarked) {
      newBookmarkedIds.delete(course.courseId);
      // Remove from myCourses if unbookmarked
      setMyCourses((prevCourses) =>
        prevCourses.filter((c) => c.courseId !== course.courseId)
      );
    } else {
      newBookmarkedIds.add(course.courseId);
      // Add to myCourses if bookmarked
      setMyCourses((prevCourses) => [...prevCourses, course]);
    }

    setBookmarkedCourseIds(newBookmarkedIds);
  };

  const handleStatusChange = (courseId, newStatus) => {
    setMyCourses((prevCourses) =>
      prevCourses.map((course) =>
        course.courseId === courseId ? { ...course, status: newStatus } : course
      )
    );

    // Reapply the filter after updating the status
    handleFilterSelect(selectedFilter);
  };

  const handleFilterSelect = (selectedFilter) => {
    setSelectedFilter(selectedFilter); // Store selected filter state
    if (selectedFilter === "All") {
      setFilteredCourses(myCourses);
    } else {
      const filtered = myCourses.filter(
        (course) =>
          course?.status?.toLowerCase() === selectedFilter.toLowerCase()
      );
      setFilteredCourses(filtered);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <WelcomeCard
        title="Recommended Courses"
        current={currentJob}
        target={targetJob}
      />
      <Elements stripe={stripePromise}>
        <VirtualCourses type="ontrack" />
      </Elements>
      {!targetJob ? (
        <div className="flex flex-col items-center justify-center text-center p-6 bg-white shadow-md rounded-lg">
          <p className="text-lg font-semibold text-gray-700">
            You haven't selected a target job yet.
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Select a target job to get personalized course recommendations.
          </p>
          <CustomButton
            label={"Go to Recommended Careers"}
            onClick={() => navigate("/dashboard/recommended-careers")}
            className="mt-4 px-6 py-2 bg-custom-blue text-white rounded-md text-sm sm:text-base"
          />
        </div>
      ) : courseListPending ? (
        <SecondaryLoader />
      ) : (
        <>
          <UpskillingPathway
            title="Online Self-Learning (Coursera)"
            learningPathwayList={courseList}
            onClick={handleClick}
            bookmarkedCourseIds={bookmarkedCourseIds}
            onBookmarkToggle={handleBookmarkToggle}
            onStatusChange={handleStatusChange}
          />
        </>
      )}
    </div>
  );
};

export default RecommendedCoursesPage;
